import {
  getActiveProposals,
  getActiveRecommenders,
  getFunderNetRecommenderAllocations,
  getRoundMemberForEvaluator,
  isFunderRatingWorkPair,
  isHold,
} from "@engine/round/accessors";
import type { Funder, RoundAggregate } from "@engine/round/aggregate";
import { meanUserId } from "@lib/constants";
import { getEvaluatorDisplayName } from "@lib/memberDisplay";
import { type GetRoundChecklistResponse } from "@lib/todoRailTypes";
import { isAssignableTrack, type Track, type UserProfiles } from "@lib/types";
import { type ProgressRow } from "@lib/videoStateTypes";
import {
  buildTodoRail,
  type TodoRail,
  type TodoRailVideo,
} from "./todoRailModel";
import { recommenderStepAvailability } from "./todoRecommenderWork";

/**
 * The catalog fields the rail assembly reads off a `getVideos` row (or the
 * server's equivalent select). Structural on purpose: both callers' rows
 * satisfy it without mapping first.
 */
export interface TodoRailVideoSource {
  id: string;
  type: string;
  status: string | null;
  track: string | null;
  filename: string;
  durationSeconds: number | null;
  activePosterId: string | null;
  posterCdnUrl: string | null;
  homeRecommenderId: string | null;
}

/**
 * Where each rail step opens. Injected because URL construction is a client
 * concern (workspace routes, deep-link hashes): the funder's drawer passes the
 * real builders, while the server's admin read passes {@link noTodoRailLinks}
 * — the admin view inspects state, it doesn't navigate the workspace.
 */
export interface TodoRailLinks {
  linkToVideo: (video: TodoRailVideo) => string | null;
  linkToRecommenderNotes: (recommenderId: string) => string | null;
  linkToRecommenderProfile: (recommenderId: string) => string | null;
  linkToRecommenderScore: (recommenderId: string) => string | null;
  linkToScoring: () => string | null;
  linkToEvaluations: () => string | null;
}

export const noTodoRailLinks: TodoRailLinks = {
  linkToVideo: () => null,
  linkToRecommenderNotes: () => null,
  linkToRecommenderProfile: () => null,
  linkToRecommenderScore: () => null,
  linkToScoring: () => null,
  linkToEvaluations: () => null,
};

/**
 * Assembles a funder's To Do rail from the reads both runtimes hold: the round
 * aggregate, the video catalog, the funder's watch progress, and their
 * checklist read. The ONE place the rail's inputs are shaped — the funder's
 * drawer (useTodoRail) and the admin read of a funder's drawer
 * (getFunderTodoRail) both call this, so what the admin sees cannot drift
 * from what the funder sees.
 *
 * `viewerTrack`/`viewerUserId` decide which recommender steps exist (whether
 * notes are readable, etc.) and must describe the person whose rail this is —
 * the funder — not whoever is looking at it.
 */
export function assembleTodoRail({
  round,
  roundId,
  funder,
  viewerTrack,
  viewerUserId,
  userProfiles,
  videos,
  progress,
  checklist,
  links,
  formatCurrency,
}: {
  round: RoundAggregate;
  roundId: string;
  funder: Funder | null;
  viewerTrack: Track;
  viewerUserId: string | undefined;
  userProfiles: UserProfiles;
  videos: TodoRailVideoSource[];
  progress: ProgressRow[];
  checklist: GetRoundChecklistResponse;
  links: TodoRailLinks;
  formatCurrency: (amount: number) => string;
}): TodoRail {
  // The rail's source sets must be the SAME sets the workspace renders, or it
  // asks for work that doesn't exist there. Third instance of that class (the
  // funder itself was the first), so both filters mirror
  // useFunderWorkspaceBaseData rather than re-deriving:
  //
  //  - the hold proposal is active and carries a default evaluated MVF, so it
  //    counts as a rateable pair while Note Ratings hides it — inflating
  //    the scoring denominator with ratings nobody can enter;
  //  - the synthetic mean recommender is an evaluator but not a person, so it
  //    produced a recommender group whose rows can never be completed and
  //    whose link resolves to somebody else entirely.
  const proposals = getActiveProposals(round).filter((p) => !isHold(round, p));
  const recommenders = getActiveRecommenders(round).filter((recommender) => {
    const member = getRoundMemberForEvaluator(round, recommender.id);
    return member?.id !== meanUserId;
  });

  // The scoring step's denominator counts only pairs the workspace actually
  // asks the funder to rate — recusals and inactive evaluations are not work
  // they can complete, and no-note (MVF-only) pairs default to a rating of 0
  // and are hidden from the Note Ratings grid, so counting them would set
  // a target the visible rows can never satisfy.
  let rated = 0;
  let rateable = 0;

  for (const recommender of recommenders) {
    for (const proposal of proposals) {
      if (!isFunderRatingWorkPair(round, recommender.id, proposal.id)) {
        continue;
      }

      rateable += 1;
      const score = funder?.scores[recommender.id]?.[proposal.id];

      if (typeof score === "number" && Number.isFinite(score)) {
        rated += 1;
      }
    }
  }

  const profileMemberIds = new Set(checklist.recommendersWithProfile);

  const railVideos: TodoRailVideo[] = videos.map((video) => ({
    id: video.id,
    type: video.type,
    status: video.status,
    track: isAssignableTrack(video.track) ? video.track : null,
    title: video.filename,
    durationSeconds: video.durationSeconds,
    activePosterId: video.activePosterId,
    posterCdnUrl: video.posterCdnUrl,
    homeRecommenderId: video.homeRecommenderId,
  }));

  return buildTodoRail({
    roundId,
    videos: railVideos,
    progress,
    marks: checklist.marks,
    recommenders: recommenders.map((recommender) => {
      const member = getRoundMemberForEvaluator(round, recommender.id);
      return {
        recommenderId: recommender.id,
        displayName: getEvaluatorDisplayName({
          round,
          evaluator: recommender,
          userProfiles,
        }),
        // Which of this recommender's steps have somewhere to go — one pure
        // decision with its own viewer-matrix coverage, because answering it
        // here instead of asking the surfaces that render has now produced
        // three separate bugs. See todoRecommenderWork.
        ...recommenderStepAvailability({
          round,
          viewerTrack,
          viewerUserId,
          memberId: member?.id ?? null,
          recommenderId: recommender.id,
          funderRecommenderWeights: funder?.recommenderWeights,
          membersWithProfileSummary: profileMemberIds,
        }),
      };
    }),
    scoring: { rated, rateable },
    // NOT `funder.budget`. That field is seeded at round creation
    // (round budget / number of funders) and is directly editable by an
    // admin, so reading it showed every funder's Evaluations step complete,
    // with a real dollar figure, before anyone had allocated anything. It
    // only coincidentally equals the allocation total after the funder's
    // first incremental-eval action. The allocations themselves are the
    // honest source, and the same one the Incremental Evaluations page uses.
    evaluations: {
      allocated:
        funder == null
          ? 0
          : Object.values(
              getFunderNetRecommenderAllocations(round, funder),
            ).reduce((sum, amount) => sum + amount, 0),
    },
    ...links,
    formatCurrency,
  });
}
